import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DoneIcon from "@mui/icons-material/Done";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import CustomDialogTitle from "../Dialogs/CustomDialogTitle";
import { appColors } from "../../constants/theme";
import {
  formatNotificationDate,
  getNotificationNavigatePath,
  getNotificationTypeLabelKey,
} from "../../utils/notificationHelpers";

const ReadStatusChip = ({ isRead }) => {
  const { t } = useTranslation();
  return (
    <Chip
      size="small"
      label={isRead ? t("notification_status_read") : t("notification_status_unread")}
      sx={{
        height: 24,
        fontWeight: 600,
        fontSize: "0.72rem",
        flexShrink: 0,
        backgroundColor: isRead ? appColors.successBg : appColors.warningBg,
        color: isRead ? appColors.successDark : appColors.warningDark,
      }}
    />
  );
};

const NotificationDetailModal = ({
  open,
  notification,
  language,
  markingId,
  onClose,
  onMarkAsRead,
  onDelete,
  onNavigate,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const lang = language ?? i18n.language;

  if (!notification) return null;

  const isRead = Boolean(notification.isRead);
  const isMarking = markingId === notification.id;
  const typeKey = getNotificationTypeLabelKey(notification.type ?? notification.data?.type);
  const createdAt = notification.createdAt ?? notification.CreatedAt;
  const navigatePath = getNotificationNavigatePath(notification);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <CustomDialogTitle title={notification.title || t("notifications")} onClose={onClose} />
      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            <ReadStatusChip isRead={isRead} />
            {typeKey && (
              <Chip
                size="small"
                label={t(typeKey)}
                sx={{
                  height: 24,
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  backgroundColor: appColors.primaryBg,
                  color: theme.palette.primary.dark,
                }}
              />
            )}
          </Box>

          {createdAt && (
            <Typography variant="caption" sx={{ color: appColors.gray500, fontSize: "0.85rem" }}>
              {formatNotificationDate(createdAt, lang)}
            </Typography>
          )}

          <Divider />

          <Typography
            sx={{
              color: appColors.gray900,
              fontSize: "0.975rem",
              lineHeight: 1.65,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {notification.body || "—"}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1, flexWrap: "wrap" }}>
        {!isRead && (
          <Button
            variant="outlined"
            startIcon={isMarking ? <CircularProgress size={16} /> : <DoneIcon />}
            disabled={isMarking}
            onClick={() => onMarkAsRead(notification.id)}
            sx={{ textTransform: "none", borderRadius: "10px" }}
          >
            {t("mark_as_read")}
          </Button>
        )}
        {navigatePath && (
          <Button
            variant="contained"
            startIcon={<OpenInNewIcon />}
            onClick={() => onNavigate(navigatePath)}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            }}
          >
            {t("go_to_notification_target")}
          </Button>
        )}
        <Button
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={() => onDelete(notification.id)}
          sx={{ textTransform: "none", borderRadius: "10px", ml: "auto" }}
        >
          {t("delete")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationDetailModal;
